import { effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { catchError, Observable, retry, tap, throwError } from 'rxjs';
import { RequestStateEnum } from '../enums/app.enum';
import { RoutesEnum } from '../enums/routes.enum';
import { UserGenderEnum } from '../enums/user.enums';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { User } from '../types/user.types';
import { AuthStore } from './auth.store';

interface UserState {
  requestState: RequestStateEnum | null;
  user: User | null;
}

const initialState: UserState = {
  requestState: null,
  user: null,
};

export const UserStore = signalStore(
  { providedIn: 'root' },

  withState(initialState),

  withMethods((store) => {
    const userService = inject(UserService);
    const authService = inject(AuthService);
    const authStore = inject(AuthStore);
    const router = inject(Router);

    return {
      _init(): void {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        authService
          .me()
          .pipe(retry({ delay: 1000, count: 3 }))
          .subscribe({
            next: (response) => {
              patchState(store, {
                user: response,
                requestState: RequestStateEnum.SUCCESS,
              });
            },
            error: () => {
              patchState(store, {
                user: null,
                requestState: RequestStateEnum.ERROR,
              });
            },
          });
      },

      updateUser(firstname: string, lastname: string, gender: UserGenderEnum): Observable<any> {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        return userService.updateUser(firstname, lastname, gender).pipe(
          tap((response) => {
            patchState(store, { requestState: RequestStateEnum.SUCCESS, user: response.payload });
          }),
          catchError((error) => {
            patchState(store, { requestState: RequestStateEnum.ERROR });
            return throwError(() => error);
          })
        );
      },

      updateState(): void {
        this._init();
      },

      deleteUser(): Observable<any> {
        patchState(store, { requestState: RequestStateEnum.PENDING });

        return userService.deleteUser().pipe(
          tap(() => {
            patchState(store, { requestState: RequestStateEnum.SUCCESS });
            authStore.setLoginState(false);
            router.navigateByUrl(RoutesEnum.COMMON_REGISTER);
          }),
          catchError((error) => {
            patchState(store, { requestState: RequestStateEnum.ERROR });
            return throwError(() => error);
          })
        );
      },
    };
  }),

  withHooks({
    onInit(store) {
      const authStore = inject(AuthStore);

      effect(() => {
        if (authStore.isLoggedIn()) {
          store._init();
        }
      });
    },
  })
);
